// Astrea Ops Platform - Team (Personnel/Auth) tab data-access layer
//
// Phase 3, users slice. Mirrors src/lib/database.js's shape and conventions. Maps
// the app's existing `users` array (one row per team member, everything TeamView/
// OnboardingForm/RoleBadge/etc already expect) onto two real tables: `people` (HR
// record - every team member and subcontractor, no password) and `profiles` (one
// row per person who can actually log in, id = auth.users.id). See
// claude/go-live-plan.md (Phase 3, users/auth slice) for the full design writeup,
// and src/lib/auth.js for the sign-in/change-password/grant-access calls that sit
// alongside this file.
//
// Why role has two homes: `profiles.role` is the live, database-enforced permission
// level (what RLS actually checks) - it only exists for people who have a real
// login. `people.intended_role` is what the HR record was onboarded/edited as, and
// is what the merged `role` field falls back to for the (currently most) people who
// have HR data but no Supabase Auth account yet, so TeamView still shows a sensible
// role badge for them. Editing a person's role in OnboardingForm always writes
// intended_role; if that person already has a profile, it also updates the live
// profiles.role so an existing account's permissions change with it.
//
// Ids: same convention as src/lib/database.js - new people get a real UUID assigned
// client-side via crypto.randomUUID() before peopleCreate() is called, since the id
// is often used synchronously (e.g. selecting the new person in a list) before the
// network round trip completes.
//
// What this file deliberately does NOT do: create or touch `profiles` rows. That is
// exclusively the admin-gated /api/create-user-account.js serverless function's job
// (it needs the Supabase service-role key to call the Auth Admin API and set a
// password), invoked via src/lib/auth.js's grantAccess(). Keeping that boundary
// hard is what makes it impossible for peopleCreate/peopleUpdate to accidentally
// grant someone a login.

import { supabase } from './supabaseClient';
import { selectAllRes } from './selectAll';
import { normalizeEmail, phoneOrNull } from './contactFormat';

// v7.74 (16 Oct 2026): emails are saved in lower case and phone numbers in the house format
// (src/lib/contactFormat.js); a person's phone keeps its own country code box, so the number is
// stored without it ("714-555-0192" with +1, "20-7946-0958" with +44). Addresses have a country.
const lowerOrNull = v => { const s = normalizeEmail(v); return s || null; };
const phoneNat = (v, cc) => { const s = String(v ?? '').trim(); if (!s) return null; return phoneOrNull(s, { cc: cc || '+1', national: true }) || s; };

// v7.63 (7 Oct 2026): tax ID numbers live in people_private, which only Senior Management
// and Finance can read (20261007000001_finance_isolation.sql). App sets this on every render.
let FINANCE_VIEW = false;
export function setPeopleFinanceView(v) { FINANCE_VIEW = !!v; }

// v7.119 (Martin, 28 Oct 2026): "Both Haydee and I have edited Katy Smith's birthday multiple times,
// yet when we navigate away from it and return it is blank." Katy had nothing on file in
// people_sensitive, so no row came back for her, she was treated as "could not see these fields",
// and every save skipped them. Senior Management, POM, Finance and System Admins read every row
// (can_see_person_sensitive() in the database), so for them a person with no row simply has nothing
// on file yet and their details can be added. App sets this from the signed-in login.
let SENSITIVE_VIEW = false;
export function setPeopleSensitiveView(v) { SENSITIVE_VIEW = !!v; }

// v7.68 (10 Oct 2026): a subcontractor's company address and mailing address (if different).
// Stored in people_private beside the tax ID, so only Senior Management and Finance read or
// write them; a member sees and changes their own through the functions at the end of this file.
const COMPANY_COLS = ['company_address_line1', 'company_address_line2', 'company_city', 'company_state', 'company_zip',
  'company_country', 'mailing_same_as_company', 'company_mailing_line1', 'company_mailing_line2', 'company_mailing_city',
  'company_mailing_state', 'company_mailing_zip', 'company_mailing_country'];
// column -> app field
const COMPANY_FIELDS = {
  company_address_line1: 'companyAddress1', company_address_line2: 'companyAddress2', company_city: 'companyCity',
  company_state: 'companyState', company_zip: 'companyZip', company_country: 'companyCountry',
  company_mailing_line1: 'companyMailing1', company_mailing_line2: 'companyMailing2', company_mailing_city: 'companyMailingCity',
  company_mailing_state: 'companyMailingState', company_mailing_zip: 'companyMailingZip', company_mailing_country: 'companyMailingCountry',
};
const COUNTRY_COLS = ['company_country', 'company_mailing_country'];
export function companyFromRow(r) {
  const out = { mailingSameAsCompany: r ? r.mailing_same_as_company !== false : true };
  Object.entries(COMPANY_FIELDS).forEach(([col, f]) => { out[f] = (r && r[col]) || (COUNTRY_COLS.includes(col) ? 'USA' : ''); });
  return out;
}

function toUser(person, profile) {
  return {
    id: person.id,
    name: person.name || '',
    email: person.astrea_email || person.email || '',
    astreaEmail: person.astrea_email || '',
    // No silent default here: falling back to a specific role (e.g. 'team_lead')
    // for someone with no real role set would show a badge that asserts something
    // false. null is honest - RoleBadge renders nothing for it - until an admin
    // assigns a real role via Edit.
    role: profile?.role || person.intended_role || null,
    archived: !!person.archived,
    legalName: person.legal_name || '',
    dob: person.dob || '',
    phone: person.phone || '',
    phoneCC: person.phone_cc || '+1',
    subCompany: person.sub_company || '',
    subTIN: person.sub_tin || '',
    subEmail: person.sub_email || '',
    insuranceExpiry: person.insurance_expiry || '',
    // v7.68: the W-9 and ICA dates on the team member form were never saved before; these
    // columns have existed on people since the first schema.
    subW9Date: person.w9_received_date || '',
    subICADate: person.ica_signed_date || '',
    emergencyName: person.emergency_name || '',
    emergencyPhone: person.emergency_phone || '',
    emergencyCC: person.emergency_cc || '+1',
    specialAccommodations: person.special_accommodations || '',
    cprExpiry: person.cpr_expiry || '',
    additionalCerts: person.additional_certs || '',
    militaryStatus: person.military_status || '',
    leStatus: person.le_status || '',
    clearanceStatus: person.clearance_status || '',
    headshot: person.headshot_url || '',
    resumeUrl: person.resume_url || '',
    cprUrl: person.cpr_url || '',
    insuranceUrl: person.insurance_url || '',
    // New fields the real-auth conversion needed - not in the old localStorage shape,
    // additive only, so nothing that reads `users` today breaks.
    hasAccess: !!profile,
    mustResetPassword: !!profile?.must_reset_password,
    profileId: profile?.id || null,
    // Independent of role - see profiles.is_system_admin in
    // 20260919000001_senior_management_and_system_admin.sql. Only meaningful when
    // hasAccess is true (no profile row, nothing to flag).
    isSystemAdmin: !!profile?.is_system_admin,
    // Subcontractor document upload (20260926000001_subcontractor_uploads.sql).
    // Null until a Senior Management/System Admin user generates a link for this
    // person from TeamView. See generateUploadToken() below and
    // src/lib/pendingUploads.js for the rest of the flow.
    uploadToken: person.upload_token || null,
    uploadTokenCreatedAt: person.upload_token_created_at || null,
  };
}

export async function loadPeople() {
  const finance = FINANCE_VIEW;
  const [peopleRes, profilesRes, privateRes, sensitiveRes] = await Promise.all([
    selectAllRes(() => supabase.from('people').select('*').order('id')),
    selectAllRes(() => supabase.from('profiles').select('*').order('id')),
    finance ? selectAllRes(() => supabase.from('people_private').select('person_id, sub_tin, ' + COMPANY_COLS.join(', ')).order('person_id')) : Promise.resolve({ data: null, error: null }),
    // v7.64: date of birth, address, passport, clearance, military/LE status and special
    // accommodations live in people_sensitive. Row-level security returns only the rows this
    // viewer may see (Senior Management / POM / Finance / System Admin, plus their own), so a
    // field role gets back nothing here and those fields stay blank for them.
    selectAllRes(() => supabase.from('people_sensitive').select('*').order('person_id')),
  ]);
  if (peopleRes.error) throw peopleRes.error;
  if (profilesRes.error) throw profilesRes.error;
  const profileByPerson = new Map((profilesRes.data || []).map(p => [p.person_id, p]));
  // If people_private could not be read (not finance, or the table is not there yet), no one
  // is marked as loaded, so a save never clears a tax ID it could not see.
  const tinOk = finance && !privateRes.error && Array.isArray(privateRes.data);
  if (finance && privateRes.error) console.error('Failed to load tax IDs:', privateRes.error);
  const tinByPerson = new Map(tinOk ? privateRes.data.map(r => [r.person_id, r.sub_tin || '']) : []);
  const privByPerson = new Map(tinOk ? privateRes.data.map(r => [r.person_id, r]) : []);
  const sensOk = !sensitiveRes.error && Array.isArray(sensitiveRes.data);
  const sensByPerson = new Map(sensOk ? sensitiveRes.data.map(r => [r.person_id, r]) : []);
  return (peopleRes.data || []).map(person => {
    const u = toUser(person, profileByPerson.get(person.id));
    if (tinOk) { u.subTIN = tinByPerson.get(person.id) || ''; u._tinLoaded = true; Object.assign(u, companyFromRow(privByPerson.get(person.id))); }
    else u.subTIN = '';
    const s = sensByPerson.get(person.id);
    if (s) {
      u.dob = s.dob || '';
      u.militaryStatus = s.military_status || '';
      u.leStatus = s.le_status || '';
      u.clearanceStatus = s.clearance_status || '';
      u.specialAccommodations = s.special_accommodations || '';
      u.mailingAddress1 = s.mailing_address_line1 || '';
      u.mailingAddress2 = s.mailing_address_line2 || '';
      u.mailingCity = s.mailing_city || '';
      u.mailingState = s.mailing_state || '';
      u.mailingZip = s.mailing_zip || '';
      u.mailingCountry = s.mailing_country || 'USA';
      u.passportExpiry = s.passport_expiry || '';
      u.backgroundCheckDate = s.background_check_date || '';
      u._sensLoaded = true;
    } else {
      // Not visible to this viewer (or none on file): keep these blank so a later save by a
      // viewer who also cannot see them does not write emptiness over real values.
      u.dob = ''; u.militaryStatus = ''; u.leStatus = ''; u.clearanceStatus = '';
      u.specialAccommodations = '';
      // v7.119: a viewer who reads every row knows there is nothing on file, so the details can be
      // filled in (the first save creates the row)
      if (sensOk && SENSITIVE_VIEW) {
        u.mailingAddress1 = ''; u.mailingAddress2 = ''; u.mailingCity = ''; u.mailingState = ''; u.mailingZip = '';
        u.mailingCountry = 'USA'; u.passportExpiry = ''; u.backgroundCheckDate = '';
        u._sensLoaded = true;
      }
    }
    return u;
  });
}

function toPersonRow(entry) {
  return {
    name: entry.name || null,
    legal_name: entry.legalName || null,
    email: lowerOrNull(entry.email),
    astrea_email: lowerOrNull(entry.astreaEmail || entry.email),
    phone: phoneNat(entry.phone, entry.phoneCC),
    phone_cc: entry.phoneCC || '+1',
    sub_company: entry.subCompany || null,
    // The database moves this into people_private (finance only) and never stores it here.
    // Sent only when the tax ID was loaded, so other saves leave it alone; '' clears it.
    sub_tin: entry._tinLoaded ? (entry.subTIN || '') : null,
    sub_email: lowerOrNull(entry.subEmail),
    insurance_expiry: entry.insuranceExpiry || null,
    w9_received_date: entry.subW9Date || null,
    ica_signed_date: entry.subICADate || null,
    emergency_name: entry.emergencyName || null,
    emergency_phone: phoneNat(entry.emergencyPhone, entry.emergencyCC),
    emergency_cc: entry.emergencyCC || '+1',
    cpr_expiry: entry.cprExpiry || null,
    additional_certs: entry.additionalCerts || null,
    headshot_url: entry.headshot || null,
    resume_url: entry.resumeUrl || null,
    cpr_url: entry.cprUrl || null,
    insurance_url: entry.insuranceUrl || null,
    intended_role: entry.role || null,
    archived: !!entry.archived,
  };
}


// v7.64: the personal details that moved to people_sensitive. Written only here, and only
// when this viewer actually loaded them (_sensLoaded) or the person is brand new, so a save
// by someone who could not see these fields never clears them. Row-level security still has
// the final say: only Senior Management and System Admins can write this table.
function sensitiveRow(entry) {
  return {
    dob: entry.dob || null,
    military_status: entry.militaryStatus || null,
    le_status: entry.leStatus || null,
    clearance_status: entry.clearanceStatus || null,
    special_accommodations: entry.specialAccommodations || null,
    mailing_address_line1: entry.mailingAddress1 || null,
    mailing_address_line2: entry.mailingAddress2 || null,
    mailing_city: entry.mailingCity || null,
    mailing_state: entry.mailingState || null,
    mailing_zip: entry.mailingZip || null,
    mailing_country: String(entry.mailingCountry || '').trim() || 'USA',
    passport_expiry: entry.passportExpiry || null,
    background_check_date: entry.backgroundCheckDate || null,
    updated_at: new Date().toISOString(),
  };
}

async function saveSensitive(entry, isNew) {
  if (!isNew && !entry._sensLoaded) return; // could not see these fields; leave them untouched
  const row = sensitiveRow(entry);
  const hasAny = Object.entries(row).some(([k, v]) => k !== 'updated_at' && k !== 'mailing_country' && v);
  if (!hasAny && isNew) return; // nothing to store for a brand-new person
  const { error } = await supabase.from('people_sensitive').upsert({ person_id: entry.id, ...row });
  // v7.119: a refusal is no longer passed over in silence - only Senior Management and System Admins
  // open Edit, so a refusal here is a real problem and the person saving is told.
  if (error) {
    console.error('Failed to save personal details:', error);
    throw new Error('The personal details (date of birth, home address, passport, background) were not saved: ' + (error.message || error.code || 'refused'));
  }
}

// entry.id must already carry a real UUID (see the module note above).
// Company addresses: written only by a viewer who loaded them (Senior Management / Finance),
// so nobody else's save can blank them. Row-level security has the final say.
async function saveCompanyAddresses(entry, isNew) {
  if (!entry._tinLoaded && !(isNew && FINANCE_VIEW)) return;
  const row = { mailing_same_as_company: entry.mailingSameAsCompany !== false, updated_at: new Date().toISOString() };
  Object.entries(COMPANY_FIELDS).forEach(([col, f]) => { row[col] = String(entry[f] || '').trim() || (COUNTRY_COLS.includes(col) ? 'USA' : null); });
  const hasAny = Object.keys(COMPANY_FIELDS).some(c => !COUNTRY_COLS.includes(c) && row[c]) || !row.mailing_same_as_company;
  if (isNew && !hasAny) return;
  const { error } = await supabase.from('people_private').upsert({ person_id: entry.id, ...row });
  if (error && error.code !== '42501') console.error('Failed to save company addresses:', error);
}

export async function peopleCreate(entry) {
  const { error } = await supabase.from('people').insert({ id: entry.id, ...toPersonRow(entry) });
  if (error) throw error;
  await saveSensitive(entry, true);
  await saveCompanyAddresses(entry, true);
}

// If entry carries a profileId (i.e. this person already has a real login), also
// syncs the live profiles.role so an existing account's permissions track an
// admin's edit here instead of silently drifting from intended_role.
export async function peopleUpdate(entry) {
  // v7.119: .select() so a save the database turned away (no permission: no row changed) is noticed
  const { data, error } = await supabase.from('people').update(toPersonRow(entry)).eq('id', entry.id).select('id');
  if (error) throw error;
  if (Array.isArray(data) && !data.length) throw new Error('The changes were not saved: the database did not accept them for this login.');
  await saveSensitive(entry, false);
  await saveCompanyAddresses(entry, false);
  if (entry.profileId && entry.role) {
    const { error: roleErr } = await supabase.from('profiles').update({ role: entry.role }).eq('id', entry.profileId);
    if (roleErr) throw roleErr;
  }
}

// Subcontractor document upload: (re)issues the token behind this person's
// ?upload=<token> link. crypto.randomUUID() twice, concatenated, gives a token
// with far more entropy than anything worth guessing - this is the sole credential
// an anonymous subcontractor holds, so it has to stand alone (see
// 20260926000001_subcontractor_uploads.sql's comment on why the lookup is
// service-role-only and never exposed to the anon client for browsing/enumeration).
export async function generateUploadToken(personId) {
  const token = (crypto.randomUUID() + crypto.randomUUID()).replace(/-/g, "");
  const { error } = await supabase
    .from("people")
    .update({ upload_token: token, upload_token_created_at: new Date().toISOString() })
    .eq("id", personId);
  if (error) throw error;
  return token;
}

// Admin-only (enforced in the UI, TeamView's "Delete Permanently" - see
// App.jsx). Permanently removes a person's HR record. assignments,
// unavailability, certifications, and time_entries rows for this person are
// all `on delete cascade` in the schema, so this also erases their staffing
// and time history across every project - real data loss, warned about
// explicitly before the caller ever gets here. profiles.person_id is
// `on delete restrict`, so this throws if the person still has an active
// login; the caller should check entry.hasAccess and have them revoked first
// rather than relying on this error alone.
export async function peopleDelete(id) {
  const { error } = await supabase.from('people').delete().eq('id', id);
  if (error) throw error;
}

// ── v7.68: a member's own details ──────────────────────────────────────────────
// The database function update_my_profile() accepts only these fields, and only for the
// signed-in person's own record (20261010000001_self_profile_company_addresses.sql).
export const MY_PROFILE_FIELDS = {
  phone: 'phone', phone_cc: 'phoneCC', sub_email: 'subEmail',
  emergency_name: 'emergencyName', emergency_phone: 'emergencyPhone', emergency_cc: 'emergencyCC',
  home_line1: 'mailingAddress1', home_line2: 'mailingAddress2', home_city: 'mailingCity', home_state: 'mailingState', home_zip: 'mailingZip',
  home_country: 'mailingCountry',
  ...COMPANY_FIELDS,
};
export async function loadMyCompanyAddresses() {
  const { data, error } = await supabase.rpc('get_my_company_addresses');
  if (error) throw error;
  return companyFromRow(data || null);
}
// fields: {appField: value} for the fields that changed. Returns {changed:[...], address_changed}.
export async function updateMyProfile(fields) {
  const p = {};
  Object.entries(MY_PROFILE_FIELDS).forEach(([key, f]) => { if (f in fields) p[key] = String(fields[f] ?? ''); });
  if ('sub_email' in p) p.sub_email = normalizeEmail(p.sub_email);
  if ('mailingSameAsCompany' in fields) p.mailing_same_as_company = fields.mailingSameAsCompany !== false;
  if (!Object.keys(p).length) return { changed: [], address_changed: false };
  const { data, error } = await supabase.rpc('update_my_profile', { p });
  if (error) throw error;
  return data || { changed: [], address_changed: false };
}
